/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, map, get } from 'lodash';

import Sidebar from './SideBar';
import TopBar from './TopBar';
import PreFetchState from './PreFetchState';
import { FilterTypes } from '../../components/Filters';
import PageHeader from '../../components/PageHeader';
import TitleBanner from '../../components/TitleBanner';
import Charts from '../../components/Charts';
import { serializeFilters } from '../../utils/filters';
import { downloadReport } from '../../utils/report-builder';
import Table from './Table';

import * as ToastNotifier from '../../components/Notification';
import reportsConfig from '../configs/reports-config';
import lookerApi from '../../services/looker-api';
import { initialState, reducer, sortByFieldType } from './reducer';

const ReportBuilder = ({
  match: {
    params: { uuid }
  },
  data,
  error,
  loading,
  filters,
  userId,
  name,
  views,
  savedReport,
  filtersActions: { getFiltersForPage },
  reportBuilderActions: {
    fetchViews,
    fetchReport,
    fetchCustomView,
    resetReport
  }
}) => {
  const [state, dispatch] = useReducer(reducer, initialState());

  const onToggleField = (toggledField, isPivot) => {
    dispatch({ type: 'TOGGLE_FIELD', toggledField, isPivot, filters });
  };

  const onToggleSection = name => {
    dispatch({ type: 'TOGGLE_GROUP', name });
  };

  const reorderList = (list, listName) =>
    dispatch({ type: 'REORDER_LIST', list, listName });

  const prepareReportSpec = () => {
    const { pivots, values } = state;
    const sorts = [];
    values.forEach(v => {
      if (!sorts.length && v.fieldType === 2) {
        sorts.push(`${v.name} desc`);
      }
    });

    const getFieldName = f => f.name;

    const body = {
      name,
      userId,
      view: 'client_crosstab',
      renderer: 'empty',
      fields: [...values.map(getFieldName), ...pivots.map(getFieldName)],
      aggregators: [],
      sorts,
      filters: serializeFilters(
        map(filters.fields, f => ({ ...f, booleanSeparator: false }))
      ),
      limit: undefined,
      pivots: pivots.map(getFieldName)
    };

    return body;
  };

  const handleSaveReport = async () => {
    const specs = prepareReportSpec();
    let toastId = '';

    try {
      toastId = ToastNotifier.requestInProgressNotification({
        title: 'Saving...'
      });
      const res = await lookerApi.updateReport(uuid, specs);
      if (isEmpty(res)) throw new Error('Error creating a new report');
      ToastNotifier.requestSuccessNotification({
        toastId,
        title: 'Saved',
        text: 'View, edit and share from My Reports screen.'
      });
    } catch (e) {
      ToastNotifier.requestErrorNotification({
        toastId,
        title: 'Oops',
        text: 'Something went wrong! Try again :)'
      });
      console.log(e);
    }
  };

  const handleSaveNewReport = async name => {
    const specs = prepareReportSpec();

    try {
      const response = await lookerApi.createReport({ ...specs, name });
      if (isEmpty(response.uuid)) {
        throw new Error('Error creating a new report');
      }
    } catch (e) {
      console.log(e);
    }
  };

  const runQuery = () => {
    dispatch({ type: 'REPORT_GENERATED', filters: filters.fields });
    fetchCustomView(prepareReportSpec());
  };

  const onClickRun = () => {
    if (loading) {
      return;
    }

    runQuery();
  };

  const exportReport = async (resultFormat, filename) => {
    try {
      const specs = prepareReportSpec();
      return await downloadReport(
        specs,
        filters.fields,
        resultFormat,
        `CVMi_${filename}_${Date.now()}`,
        resultFormat === 'json' ? false : true
      );
    } catch (e) {
      console.log(e);
    }
  };

  const updateFields = () => {
    dispatch({ type: 'FETCHED_FIELDS', view: get(views, 'default', {}) });
  };

  const clearReport = () => {
    dispatch({ type: 'CLEAR_REPORT' });
    resetReport();
    updateFields(views);
  };

  useEffect(() => {
    getFiltersForPage(reportsConfig.REPORT_BUILDER_REPORT_PAGE_ID);
    fetchViews();

    document.title = 'Report Builder | CVM Insights';

    return clearReport;
  }, []);

  useEffect(() => {
    dispatch({ type: 'CHECK_CURRENT_STATE', filters });
  }, [filters]);

  useEffect(() => {
    dispatch({ type: 'DATA_UPDATED', data });
  }, [data]);

  useEffect(() => {
    updateFields(views);
  }, [views]);

  useEffect(() => {
    if (savedReport && savedReport.uuid && savedReport.uuid === uuid) {
      dispatch({
        type: 'LOAD_SAVED_REPORT',
        savedReport
      });
    }
  }, [savedReport]);

  useEffect(() => {
    if (
      Object.keys(get(state, ['config', 'fields'], {})).length > 0 &&
      uuid &&
      !state.triggerFetch
    ) {
      fetchReport(uuid);
    }
  }, [state.config.fields, uuid]);

  useEffect(() => {
    if (state.triggerFetch) {
      fetchCustomView(prepareReportSpec());
    }
  }, [state.triggerFetch]);

  return (
    <div className="tvt-report-builder">
      <div className="tvt-report-builder__header-left">
        <TitleBanner>
          <PageHeader title="Report Builder" />
        </TitleBanner>
      </div>
      {savedReport && savedReport.name ? (
        <div className="tvt-report-builder__header-right">
          <div className="tvt-report-builder__header-right-title">
            <span className="cvmi-bold-text">CUSTOM REPORT</span>:{' '}
            {savedReport.name}
          </div>
        </div>
      ) : null}
      <div className="tvt-report-builder__inner-wrapper">
        <Sidebar
          filters={filters}
          pivots={state.pivots}
          config={state.config}
          canClear={
            (state.values.length > 0 || state.pivots.length > 0) &&
            !savedReport.uuid
          }
          onToggleField={onToggleField}
          clearReport={clearReport}
          onToggleFieldGroup={onToggleSection}
        />

        <div className="tvt-report-builder__wrapper">
          <div className="tvt-report-builder__wrapper-top">
            <TopBar
              onClickRun={onClickRun}
              handleSaveNewReport={handleSaveNewReport}
              handleSaveReport={handleSaveReport}
              isStateGenerated={state.currentStateRendered}
              loading={loading}
              hasData={data.length > 0}
              reportName={name}
              downloadFunc={exportReport}
              uuid={uuid}
              reportUserId={get(savedReport, 'userId', '')}
              userId={userId}
              disableSaveAs={
                state.values.length < 1 ||
                (state.values.length > 1 && !state.currentStateRendered)
              }
            />
          </div>
          <Charts
            data={data}
            fields={state.generatedReport.values}
            hasPivots={state.generatedReport.pivots.length > 0}
            loading={loading}
            rendered={state.currentStateRendered}
          >
            {state.showTable && (
              <Table
                data={data}
                columnHeaders={state.tableColumns}
                fields={state.generatedReport.values.sort(sortByFieldType)}
                pivots={state.generatedReport.pivots}
                error={typeof get(error, 'error', null) === 'string'}
                loading={loading || data.length < 1}
              />
            )}
            {!state.showTable && (
              <PreFetchState
                fields={state.config.fields}
                toggleField={onToggleField}
                reorderList={reorderList}
                hasView={!isEmpty(state.config)}
                hasAnyActiveField={false}
                values={state.values}
                pivots={state.pivots}
                pivotDragDisabled={false}
              />
            )}
          </Charts>
        </div>
      </div>
    </div>
  );
};

ReportBuilder.propTypes = {
  views: PropTypes.shape({}).isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  state: PropTypes.shape({}).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      uuid: PropTypes.string
    })
  }),
  history: PropTypes.shape({
    push: PropTypes.func
  }),
  userId: PropTypes.string.isRequired,

  filters: PropTypes.shape({
    fields: FilterTypes.FilterSetType, // eslint-disable-line react/no-typos
    groupings: FilterTypes.GroupingSetType // eslint-disable-line react/no-typos
  }).isRequired,

  filterValues: PropTypes.shape({}).isRequired,

  filtersActions: PropTypes.shape({
    clearFilter: PropTypes.func.isRequired,
    clearFilters: PropTypes.func.isRequired,
    getFiltersForPage: PropTypes.func.isRequired,
    updateFilters: PropTypes.func.isRequired
  }).isRequired,

  loading: PropTypes.bool.isRequired,

  reportBuilderActions: PropTypes.shape({
    changeData: PropTypes.func.isRequired,
    fetchCustomView: PropTypes.func.isRequired,
    fetchViews: PropTypes.func.isRequired,
    ignoreCustomViewRequest: PropTypes.func.isRequired
  }).isRequired
};

ReportBuilder.defaultProps = {
  match: {},
  history: {
    push: () => {}
  }
};

export default ReportBuilder;
